import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { MateriauxLigne } from "@/components/MateriauxOrigine";
import { RealisationCard, Lightbox, imgUrl, altOf } from "@/components/Galerie";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { useSeo, breadcrumb, absolute } from "@/lib/seo";

const tailleDe = (d) => {
  const n = parseInt(String(d || "").replace(/\D+/g, " ").trim().split(" ")[0], 10);
  if (!n) return null;
  return n <= 40 ? "Petit (≤ 40 cm)" : n <= 50 ? "Moyen (45 – 50 cm)" : "Grand (> 50 cm)";
};

const Filtre = ({ label, options, value, onChange, testid }) => options.length < 2 ? null : (
  <div className="flex flex-wrap items-center gap-2" data-testid={`filtre-${testid}`}>
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-fumee mr-1">{label}</span>
    {["Tous", ...options].map((o) => {
      const active = (value || "Tous") === o;
      return <button key={o} type="button" onClick={() => onChange(o === "Tous" ? null : o)} data-testid={`filtre-${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors duration-300 ${active ? "bg-earth text-sand border-earth" : "border-wood/25 text-bark hover:border-terra"}`}>{o}</button>;
    })}
  </div>
);

const Galerie = () => {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ peau: null, taille: null, forme: null, lacage: null });
  const [open, setOpen] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => { api.realisations().then(setItems).catch(() => setItems([])); }, []);

  const options = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
    const utile = (arr) => { const o = uniq(arr); return o.length >= 2 && o.length < arr.filter(Boolean).length ? o : []; };
    const all = items || [];
    return { peau: utile(all.map((r) => r.peau)), taille: utile(all.map((r) => tailleDe(r.diametre))), forme: utile(all.map((r) => r.forme)), lacage: utile(all.map((r) => r.lacage)) };
  }, [items]);
  const visibles = (items || []).filter((r) => (!f.peau || r.peau === f.peau) && (!f.taille || tailleDe(r.diametre) === f.taille) && (!f.forme || r.forme === f.forme) && (!f.lacage || r.lacage === f.lacage));
  const aFiltres = Object.values(options).some((o) => o.length >= 2);

  useSeo({
    title: "Tambours réalisés — Galerie des créations · Entre les Mondes, Orbe",
    description: "Découvrez les tambours traditionnels déjà créés à l'atelier d'Orbe ou lors des stages : peaux, diamètres, laçages, histoires. Une galerie pour imaginer votre propre tambour.",
    image: items?.[0]?.photos?.[0] ? imgUrl(items[0].photos[0].url) : IMAGES.mains,
    jsonLd: items?.length ? [breadcrumb([["Accueil", "/"], ["Tambours réalisés", "/tambours-realises"]]), { "@type": "ImageGallery", name: "Tambours réalisés — Entre Les Mondes", url: absolute("/tambours-realises"), image: items.slice(0, 20).map((r) => ({ "@type": "ImageObject", contentUrl: imgUrl(r.photos[0].url), name: r.titre, description: altOf(r, r.photos[0]) })) }] : null,
  });

  return (
    <div className="pt-36 pb-24" data-testid="galerie-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">Galerie</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bark leading-[1.05]">Tambours réalisés</h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-fumee leading-relaxed">Chaque tambour né ici — à l'atelier ou entre les mains d'un·e participant·e — est unique : une peau, un cadre, un laçage, une intention. Cette galerie n'est pas un catalogue : c'est une manière d'imaginer le vôtre, et de voir ce que d'autres ont fait sien.</p>
          </div>
        </Reveal>

        {items && aFiltres && (
          <Reveal className="flex flex-col gap-3 mb-12 pb-8 border-b border-sand-dark" data-testid="galerie-filtres">
            <Filtre label="Peau" options={options.peau} value={f.peau} onChange={(v) => setF({ ...f, peau: v })} testid="peau" />
            <Filtre label="Taille" options={options.taille} value={f.taille} onChange={(v) => setF({ ...f, taille: v })} testid="taille" />
            <Filtre label="Forme" options={options.forme} value={f.forme} onChange={(v) => setF({ ...f, forme: v })} testid="forme" />
            <Filtre label="Laçage" options={options.lacage} value={f.lacage} onChange={(v) => setF({ ...f, lacage: v })} testid="lacage" />
          </Reveal>
        )}

        {!items && <div className="py-24 flex justify-center text-fumee"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {items && items.length === 0 && (
          <div className="py-20 text-center border border-dashed border-wood/30 rounded-2xl" data-testid="galerie-vide">
            <p className="font-serif text-2xl text-bark">La galerie se remplit peu à peu.</p>
            <p className="text-fumee mt-3 max-w-md mx-auto">Les premiers tambours réalisés arrivent bientôt. En attendant, la boutique montre les pièces disponibles à l'adoption.</p>
            <Link to="/boutique" className="inline-flex items-center gap-2 mt-6 text-terra text-sm hover:gap-3 transition-all" data-testid="galerie-vide-boutique">Voir la boutique <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
        {items && items.length > 0 && visibles.length === 0 && <p className="py-16 text-center text-fumee" data-testid="galerie-aucun">Aucun tambour ne correspond à ces critères — <button className="text-terra underline-offset-4 hover:underline" onClick={() => setF({ peau: null, taille: null, forme: null, lacage: null })}>réinitialiser les filtres</button>.</p>}
        {visibles.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="galerie-grille">
            {visibles.map((r, i) => <RealisationCard key={r.id} r={r} index={i} onOpen={(item, k) => { setOpen(item); setIdx(k); }} />)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-28" data-testid="galerie-cta">
        <Reveal className="bg-earth text-sand rounded-3xl p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-sand/60 mb-5">Et le vôtre ?</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">Un tambour se pense, puis se fabrique — <em className="not-italic text-terra">et enfin se pratique.</em></h2>
            <p className="text-sand/70 mt-5 max-w-xl leading-relaxed">Imaginez-le avec Ludovic pour une création sur mesure, ou venez le fabriquer vous-même en une journée à l'atelier. Dans les deux cas, il repart avec vous — et la pratique commence.</p>
            <MateriauxLigne dark className="mt-6 max-w-xl" testid="materiaux-ligne-galerie" />
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Link to="/tambour-sur-mesure" data-testid="galerie-cta-imaginer" className="inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full border border-sand/40 text-sand text-sm hover:bg-sand/10 transition-colors duration-500">Imaginer votre tambour <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/offres/stage-fabrication-tambour" data-testid="galerie-cta-creer" className="inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-terra text-earth text-sm hover:bg-sand hover:text-earth transition-colors duration-500">Créer votre tambour <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </section>

      <Lightbox item={open} index={idx} onClose={() => setOpen(null)} onIndex={setIdx} />
    </div>
  );
};

export default Galerie;
